import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
  Link,
  Typography
} from '@mui/material';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getIcon } from '../../api/aws/s3';
import { editStaffUser, usersRef } from '../../api/firebase/firestore/user';
import { clientStaffEditSchema } from '../../api/firebase/firestore/user/validation';
import noImageIcon from '../../components/assets/noImageIcon.svg';
import {
  blackText,
  linkColor,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  sizeBelowTenMb,
  textMb,
  titleMb,
  w100
} from '../../constants/Location';
import { role, screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomRadioButton from './CustomRadioButton';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';

const styles = {
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginBottom: '16px'
  },
  button: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '148px'
    },
    height: '42px',
    width: '192px'
  },
  buttonFont,
  buttonWrapper: {
    columnGap: '15px',
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  cardContentPadding,
  cardSize,
  deleteButton: {
    cursor: 'pointer'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalDesc: {
    fontSize: '16px',
    whiteSpace: 'pre-wrap'
  },
  modalText: {
    fontSize: '22px',
    padding: 0
  },
  modalTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px'
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute'
  },
  submitButton: {
    height: '42px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  w100
};

interface EditStaffType {
  iconId: string;
  email: string;
  name: string;
  department: string;
  role: boolean | string;
  icon: File;
}

interface Props {
  screen: number;
}

const StaffEditCard: React.FC<Props> = (props) => {
  const { screen } = props;
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string>('');
  const [uid] = useState<string>((location.state as { id: string })?.id);
  const [mail, setMail] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<EditStaffType>({
    defaultValues: {
      department: '',
      iconId: '',
      name: '',
      role: false
    },
    resolver: yupResolver(clientStaffEditSchema)
  });

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const currentUserRef = doc(usersRef, currentUser.uid);
        const currentUserSnap = await getDoc(currentUserRef);
        const currentUserRole = currentUserSnap.data()?.role as number;
        if (currentUserRole === role.member) {
          navigate('/auth/login');
        }
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (uid) {
        const userRef = doc(usersRef, uid);
        const userSnap = await getDoc(userRef);
        const iconImg = await getIcon(userSnap);
        setPreviewIcon(iconImg === noImageIcon ? '' : iconImg);
        const user = userSnap.data();
        if (user) {
          reset({
            department: user?.department as string,
            email: user?.email as string,
            iconId: user?.iconId as string,
            name: user?.name as string,
            role: Boolean(Number(user?.role) === 1)
          });
          setMail(user?.email as string);
          setCompanyId(user?.companyId as string);
        }
      }
      setLoading(false);
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('name') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        watch('role') === undefined ||
        Boolean(errors.name) ||
        Boolean(errors.icon) ||
        Boolean(errors.department) ||
        Boolean(iconSizeError)
      );
    }
    return false;
  };

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [iconSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError(t('validation.error.imageSize'))
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadIconFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);

      // 対象のユーザー情報を確認
      if (watch('role') === 'false') {
        const userRef = doc(usersRef, uid);
        const userSnap = await getDoc(userRef);
        const user = userSnap.data();
        // 会社IDでauthority=1の件数が2件以上であることを確認する
        const q = query(
          usersRef,
          where('companyId', '==', companyId),
          where('role', '==', 1),
          where('registerStatus', '==', 1)
        );
        const querySnapshot = await getDocs(q);
        if (
          querySnapshot.size > 0 &&
          querySnapshot.size < 2 &&
          user?.role === 1
        ) {
          throwError(t('staff.delete.alert.ng'));
          return;
        }
      }
      await editStaffUser(uid, watch, s3FileIcon);
      setOpen(true);
      setLoading(false);
      setInfoText(t('staff.edit.alert.info'));
      setTimeout(() => {
        if (screen === screenType.client) {
          navigate('/client/mypage/staff');
        } else if (screen === screenType.agent) {
          navigate('/agent/mypage/staff');
        } else if (screen === screenType.publisher) {
          navigate('/publisher/mypage/staff');
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (e) {
      const error = e as Error;
      setOpen(true);
      setLoading(false);
      setModalOpen(false);
      if (error.message === t('staff.delete.alert.ng')) {
        setErrorText(t('staff.delete.alert.ng'));
      } else {
        setErrorText(t('staff.edit.alert.error'));
      }
      throwError(e);
    } finally {
      setFirstSubmit(false);
    }
  };

  const [modalOpen, setModalOpen] = React.useState(false);

  const modalClose = (): void => {
    setModalOpen(false);
  };

  const onSubmitDelete = async (): Promise<void> => {
    try {
      setLoading(true);
      // 対象のユーザー情報を確認
      const userRef = doc(usersRef, uid);
      const userSnap = await getDoc(userRef);
      const user = userSnap.data();
      // 会社IDでauthority=1の件数が2件以上であることを確認する
      const q = query(
        usersRef,
        where('companyId', '==', companyId),
        where('role', '==', 1),
        where('registerStatus', '==', 1)
      );
      const querySnapshot = await getDocs(q);
      if (
        querySnapshot.size > 0 &&
        querySnapshot.size < 2 &&
        user?.role === 1
      ) {
        throwError(t('staff.delete.alert.ng'));
      } else {
        // 件数が2件以上の場合は、削除処理を行う
        const functions = getFunctions();
        const deleteUser = httpsCallable(functions, 'deleteUser');
        await deleteUser({ uid });
        setLoading(false);
        if (screen === screenType.client) {
          navigate('/client/mypage/staff/delete/complete');
        } else if (screen === screenType.agent) {
          navigate('/agent/mypage/staff/delete/complete');
        } else if (screen === screenType.publisher) {
          navigate('/publisher/mypage/staff/delete/complete');
        }
      }
    } catch (e: unknown) {
      const error = e as Error;
      setOpen(true);
      setLoading(false);
      setModalOpen(false);
      if (error.message === t('staff.delete.alert.ng')) {
        setErrorText(t('staff.delete.alert.ng'));
      } else {
        setErrorText(t('staff.delete.alert.error'));
      }
      throwError(e);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('staff.edit.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={iconSizeError}
                    helperText={errors.icon?.message}
                    onChange={(value: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(value);
                      changeUploadIconFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={t('staff.edit.email')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder="xxx@en-broker.co.jp"
                    disabled
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText={t('staff.edit.name')}
                    requiredText={t('form.required')}
                    isRequired
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText={t('staff.edit.department')}
                    requiredText={t('form.required')}
                    isRequired
                  />
                )}
                control={control}
                name="department"
              />
              <Controller
                render={({ field }) => (
                  <CustomRadioButton
                    {...field}
                    error={Boolean(errors.role)}
                    helperText={errors.role?.message}
                    labelText={t('staff.edit.role')}
                    requiredText={t('form.required')}
                    isRequired
                    trueOption={t('staff.edit.admin')}
                    falseOption={t('staff.edit.member')}
                    guideText={`${t('staff.edit.roleGuide.admin')}\n\n${t(
                      'staff.edit.roleGuide.member'
                    )}`}
                  />
                )}
                control={control}
                name="role"
              />
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[primaryButtonBackground, styles.submitButton]}
                onClick={() => {
                  void (async () => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (iconSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 15 }}>
                  {t('staff.edit.button')}
                </Typography>
              </Button>
              <Typography
                align="center"
                sx={[textMb, { mt: '16px' }]}
                component="div"
              >
                <Link
                  sx={[linkColor, styles.deleteButton]}
                  onClick={() => setModalOpen(true)}
                >
                  {t('staff.edit.stop')}
                </Link>
              </Typography>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
            infoText={infoText}
            sx={styles.snackbar}
          />
        </Grid>
      </Grid>
      <Dialog onClose={modalClose} open={modalOpen} sx={styles.modalWrapper}>
        <DialogContent sx={styles.modal}>
          <Typography component="div" sx={styles.modalTitle}>
            <CloseIcon onClick={modalClose} sx={{ cursor: 'pointer' }} />
          </Typography>
          <Grid container justifyContent="center" sx={{ margin: '24px 0' }}>
            <Typography component="p" sx={styles.modalDesc}>
              {mail}
              {t('staff.delete.description')}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            alignItems="center"
            sx={styles.buttonWrapper}
          >
            <Button
              variant="contained"
              sx={[secondaryButtonnBackground, styles.button]}
              onClick={() => {
                modalClose();
              }}
            >
              <Typography
                component="div"
                color={primarycolor}
                sx={styles.buttonFont}
              >
                {t('staff.cancel')}
              </Typography>
            </Button>
            <Button
              variant="contained"
              sx={[primaryButtonBackground, styles.button]}
              onClick={() => {
                void (async () => {
                  await onSubmitDelete();
                })();
              }}
            >
              <Typography component="div" color="white" sx={styles.buttonFont}>
                {t('staff.delete.button')}
              </Typography>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StaffEditCard;
